import React, { useEffect, useState } from "react";
import { Input, Button, Pagination, Spin, message } from "antd";
import { SearchOutlined, PlusOutlined } from "@ant-design/icons";
import { Gem, Coins, Wallet, Rocket, Loader, CheckCheck, Megaphone } from "lucide-react";
import PromoCard from "../PromoCard/PromoCard";
import { useDispatch } from "react-redux";
import { setSelectedPromoID } from "../../store/slices/selectedPromoIDSlice";
import api from "../../api/promosApi";
import { updatePromoFields } from "../../store/slices/promoFormSlice";
import { openFullDrawer } from "../../store/slices/fullDrawerSlice";
import PromoDrawLayer from "../../layers/DrawerLayers/PromoDrawerLayer/PromoDrawLayer";

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const searchInputClass =
    "!rounded-xl !border-white/10 !bg-white/[0.06] !pl-3 !text-white/90 hover:!border-white/[0.18] focus:!border-emerald-400/50 [&_.ant-input]:!text-white/90 placeholder:!text-white/35 [&_.anticon]:!text-emerald-400/80";

const filterBtn = (active, tone) =>
    cn(
        "!flex !h-auto !items-center !gap-1.5 !rounded-xl !border !px-3 !py-2 !text-[13px] !font-medium !transition-colors",
        active
            ? tone === "status"
                ? "!border-emerald-400/45 !bg-emerald-500/20 !text-emerald-100"
                : "!border-amber-400/45 !bg-amber-500/20 !text-amber-100"
            : "!border-white/10 !bg-white/[0.06] !text-white/75 hover:!border-white/20 hover:!bg-white/10 hover:!text-white/90"
    );

export default function PromoList() {
    const [searchQuery, setSearchQuery] = useState("");
    const [activeStatus, setActiveStatus] = useState("all");
    const [activeType, setActiveType] = useState("all");
    const [promos, setPromos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const dispatch = useDispatch();

    const statusFilters = [
        { key: "all", label: "Все", icon: <Loader size={16} /> },
        { key: "PENDING", label: "Ожидание", icon: <Loader size={16} /> },
        { key: "ACTIVE", label: "Активно", icon: <Rocket size={16} /> },
        { key: "COMPLETED", label: "Завершено", icon: <CheckCheck size={16} /> },
    ];

    const typeFilters = [
        { key: "all", label: "Все", icon: <Coins size={16} /> },
        { key: "onlyCrystals", label: "Crystals", icon: <Gem size={16} /> },
        { key: "crystalsAndCrypto", label: "Both", icon: <Coins size={16} /> },
        { key: "cryptoOnly", label: "Crypto", icon: <Wallet size={16} /> },
    ];

    const fetchPromos = async () => {
        try {
            setLoading(true);

            if (searchQuery.length > 1) {
                const results = await api.promosApi.searchPromos(searchQuery.trim());
                setPromos(results);

                setTotal(results.length);
            } else {
                const response = await api.promosApi.getPaginatedPromos({
                    page,
                    limit,
                    status: activeStatus === "all" ? undefined : activeStatus,
                    promoType: activeType === "all" ? undefined : activeType,
                });
                setPromos(response.data);
                setTotal(response.total);
            }
        } catch (e) {
            console.error("Ошибка при получении промо-акций:", e);
            message.error("Ошибка при получении промо");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
    }, [searchQuery, activeStatus, activeType]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchPromos();
        }, searchQuery.length > 1 ? 500 : 0);

        return () => clearTimeout(timeout);
    }, [searchQuery, page, activeStatus, activeType, limit]);

    const createNewPromo = (e) => {
        if (e) e.stopPropagation();
        dispatch(
            updatePromoFields({
                title: "octo",
                id: 0,
                participantsCount: 300,
                prizePlacesCount: 100,
                totalAmount: 200,
                currentAmount: 0,
                status: "PENDING",
                winnersCount: 0,
                durationDays: 0,
                tokenType: "USDT",
                creatorUserId: 1,
                link: "put here link telegram channel or group",
                maxParticipantsCount: 0,
                promoType: "crystalsAndCrypto",
                promoters: [],
                category: {
                    category_name: "3 Basic",
                    positions: [
                        {
                            winPercent: 10.0,
                            winners: 10,
                            budgetPercent: 33.0,
                            budget: 66,
                            dropPerWinner: 6.6,
                        },
                        {
                            winPercent: 23.0,
                            winners: 23,
                            budgetPercent: 33.0,
                            budget: 66,
                            dropPerWinner: 2.8696,
                        },
                        {
                            winPercent: 67.0,
                            winners: 67,
                            budgetPercent: 34.0,
                            budget: 66,
                            dropPerWinner: 0.9851,
                        },
                    ],
                },
            })
        );

        dispatch(openFullDrawer(<PromoDrawLayer create={true} />));
    };

    const totalPages = Math.max(1, Math.ceil(total / limit) || 1);

    return (
        <div
            onClick={() => dispatch(setSelectedPromoID(null))}
            className="flex h-full min-h-0 flex-1 flex-col gap-4 overflow-hidden p-4 text-white sm:p-5"
        >
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-emerald-500/10 blur-3xl" />
                {/* Заголовок · поиск · Create — в одном ряду на lg */}
                <div className="relative flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
                    <div className="flex min-w-0 shrink-0 items-center gap-3 lg:max-w-[min(100%,280px)]">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                            <Megaphone className="h-5 w-5 text-emerald-300/90" strokeWidth={2} />
                        </span>
                        <div className="min-w-0">
                            <h1 className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">Промо</h1>
                            <p className="text-[12px] leading-snug text-white/45">
                                Список, фильтры и создание
                            </p>
                        </div>
                    </div>
                    <div className="min-w-0 w-full flex-1 lg:w-auto">
                        <Input
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            prefix={<SearchOutlined />}
                            placeholder="Поиск: токен, имя или ссылка промоутера"
                            className={cn(searchInputClass, "!h-10")}
                            size="large"
                            allowClear
                            aria-label="Поиск промо"
                        />
                    </div>
                    <Button
                        onClick={createNewPromo}
                        icon={<PlusOutlined />}
                        type="primary"
                        className="!h-10 !w-full !shrink-0 !rounded-xl !border-0 !bg-emerald-500 !px-4 !text-[#070A12] hover:!bg-emerald-400 sm:!w-auto"
                    >
                        Create Promo
                    </Button>
                </div>

                {/* Только фильтры */}
                <div className="relative mt-5 border-t border-white/10 pt-5">
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-0 lg:divide-x lg:divide-white/10">
                        <div className="flex min-w-0 flex-col lg:pr-6">
                            <span className="mb-2 block h-4 leading-4 text-[11px] font-semibold uppercase tracking-wider text-white/45">
                                Статус
                            </span>
                            <div className="flex flex-wrap gap-2">
                                {statusFilters.map((f) => (
                                    <Button
                                        key={f.key}
                                        type="text"
                                        onClick={() => {
                                            setActiveStatus(f.key);
                                            setPage(1);
                                        }}
                                        className={filterBtn(activeStatus === f.key, "status")}
                                    >
                                        {f.icon} {f.label}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        <div className="flex min-w-0 flex-col border-t border-white/10 pt-5 lg:border-t-0 lg:pl-6 lg:pt-0">
                            <span className="mb-2 block h-4 leading-4 text-[11px] font-semibold uppercase tracking-wider text-white/45">
                                Тип
                            </span>
                            <div className="flex flex-wrap gap-2">
                                {typeFilters.map((f) => (
                                    <Button
                                        key={f.key}
                                        type="text"
                                        onClick={() => {
                                            setActiveType(f.key);
                                            setPage(1);
                                        }}
                                        className={filterBtn(activeType === f.key, "type")}
                                    >
                                        {f.icon} {f.label}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className={cn(shellClass, "relative flex min-h-0 flex-1 flex-col overflow-hidden")}>
                {loading && promos.length > 0 && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-[#070A12]/45 backdrop-blur-sm">
                        <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                    </div>
                )}
                <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
                    {loading && promos.length === 0 ? (
                        <div className="flex min-h-[200px] items-center justify-center">
                            <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-4">
                            {promos.length > 0 ? (
                                promos.map((promo) => <PromoCard key={promo.id} promo={promo} />)
                            ) : (
                                <p className="w-full py-12 text-center text-[13px] text-white/45">
                                    Нет подходящих промо
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {searchQuery.length === 0 && (
                <div
                    className={cn(
                        shellClass,
                        "flex shrink-0 flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5"
                    )}
                >
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                        <span className="text-[13px] text-white/50">
                            Стр. {page} из {totalPages}
                        </span>
                        <span className="text-[12px] text-white/35">
                            Всего: {total} · на странице: {limit}
                        </span>
                    </div>
                    <Pagination
                        current={page}
                        pageSize={limit}
                        total={total}
                        onChange={(newPage) => setPage(newPage)}
                        onShowSizeChange={(current, size) => {
                            setLimit(size);
                            setPage(1);
                        }}
                        showSizeChanger={true}
                        pageSizeOptions={["10", "20", "50", "100"]}
                        showLessItems
                        responsive
                        showQuickJumper={total > 100}
                        showTotal={(t, range) => `${range[0]}-${range[1]} из ${t}`}
                        className="pagination-custom !mb-0 flex flex-wrap justify-end"
                        size="small"
                    />
                </div>
            )}
        </div>
    );
}
